import createCachedSelector from 're-reselect';

import { getPersistedState } from '../../persistedState';
import { FeatureRollout, YammerState } from '../../state/types';
import { getPersistKey } from '../persistConfig/selectors';
import { isFlightControlPanelEnabledInConfig } from '../session/selectors';

const getTreatments = (state: YammerState) => state.ecsFeatureRollouts.treatments;
const getTreatmentsFromService = (state: YammerState) => state.ecsFeatureRollouts.treatmentsFromService;
const getTreatmentOverrides = (state: YammerState) => state.ecsFeatureRollouts.treatmentOverrides;

type IsFeatureRolloutEnabled = (state: YammerState, id: FeatureRollout) => boolean;
export const isFeatureRolloutEnabled: IsFeatureRolloutEnabled = (state, id) => getTreatments(state)[id];

type IsFeatureRolloutEnabledFromService = (state: YammerState, id: FeatureRollout) => boolean | undefined;
export const isFeatureRolloutEnabledFromService: IsFeatureRolloutEnabledFromService = (state, id) =>
  getTreatmentsFromService(state)[id];

const getPersistedFeatureRolloutTreatments = async (key: string) => {
  const state = await getPersistedState(key);

  return state ? state.ecsFeatureRollouts : undefined;
};

type IsPersistedFeatureRolloutEnabled = (key: string, featureId: FeatureRollout) => Promise<boolean>;
export const isPersistedFeatureRolloutEnabled: IsPersistedFeatureRolloutEnabled = async (key, featureId) => {
  const featureRollouts = await getPersistedFeatureRolloutTreatments(key);

  return featureRollouts ? !!featureRollouts.treatments[featureId] : false;
};

type DoPersistedFeatureRolloutsExist = (key: string) => Promise<boolean>;
export const doPersistedFeatureRolloutsExist: DoPersistedFeatureRolloutsExist = async (key) => {
  const persistedRollouts = await getPersistedFeatureRolloutTreatments(key);

  return persistedRollouts != null;
};

type IsFeatureRolloutEnabledInStateOrPersistedState = (
  state: YammerState,
  featureId: FeatureRollout
) => Promise<boolean>;
export const isFeatureRolloutEnabledInStateOrPersistedState: IsFeatureRolloutEnabledInStateOrPersistedState = async (
  state,
  featureId
) => {
  if (isFeatureRolloutEnabled(state, featureId)) {
    return true;
  }

  const persistKey = getPersistKey(state);

  return persistKey ? isPersistedFeatureRolloutEnabled(persistKey, featureId) : false;
};

type HasFeatureRolloutOverride = (state: YammerState, id: FeatureRollout) => boolean;
export const hasFeatureRolloutOverride: HasFeatureRolloutOverride = (state, id) =>
  getTreatmentOverrides(state)[id] !== undefined;

export const getFeatureRolloutsOverview = createCachedSelector(
  getTreatments,
  getTreatmentOverrides,
  (treatments, overrides) =>
    (Object.keys(treatments) as FeatureRollout[])
      .filter((name: FeatureRollout) => name in FeatureRollout)
      .map((name: FeatureRollout) => ({
        name,
        hasLocalOverride: overrides[name] !== undefined,
        isControl: !treatments[name],
      }))
)(() => 'cache');

type IsFlightControlPanelEnabled = (state: YammerState) => boolean;
export const isFlightControlPanelEnabled: IsFlightControlPanelEnabled = (state) =>
  isFeatureRolloutEnabled(state, FeatureRollout.FlightControlPanel) || isFlightControlPanelEnabledInConfig(state);
